import React, { useState } from 'react'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import {
  ActionIcon,
  Group,
  Modal,
  ScrollArea,
  Table,
  Text,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import {
  DragHandleDots2Icon,
  Pencil1Icon,
  TrashIcon,
} from '@radix-ui/react-icons'

import { EditExhibitModal } from './EditExhibitModal'
import {
  ExhibitFilePreviewModal,
  exhibitPreviewFile,
  PreviewFile,
} from './ExhibitFilePreview'
import {
  ClampedText,
  ExhibitFileLink,
  ReadOnlyExhibitIcon,
} from './ExhibitParts'
import { DESIGNATION_LABELS, INCLUSION_LABELS } from './exhibits'

import {
  CourtReporterExhibit,
  CourtReporterExhibitSubmission,
} from '@/apiClient'
import { useModals } from '@/hooks/useModals'
import {
  ExhibitSubmissionEvent,
  useMoveExhibitMutation,
  useRemoveExhibitMutation,
} from '@/queries/courtReporterExhibits'
import { getApiErrorMessage } from '@/utils/apiError'

export const ExhibitsTableModal: React.FC<{
  event: ExhibitSubmissionEvent
  submission: CourtReporterExhibitSubmission
  opened: boolean
  onClose: () => void
  actions: React.ReactNode
}> = ({ event, submission, opened, onClose, actions }) => {
  const [editing, setEditing] = useState<CourtReporterExhibit | null>(null)
  const [previewing, setPreviewing] = useState<PreviewFile | null>(null)
  const { mutate: move } = useMoveExhibitMutation(event)

  return (
    <>
      <Modal
        opened={opened && editing === null && previewing === null}
        onClose={onClose}
        title="Exhibits"
        size="90%"
        centered
        // A dragged row is positioned fixed, which a scaling transition would offset.
        transitionProps={{ transition: 'fade' }}
      >
        <Group position="apart" mb="md">
          <Text size="sm" color="dimmed">
            {submission.can_submit
              ? 'Drag your exhibits into the order they are marked on the record.'
              : 'These exhibits can no longer be changed.'}
          </Text>
          <Group spacing="xs">{actions}</Group>
        </Group>

        {submission.exhibits.length === 0 ? (
          <Text size="sm" color="dimmed">
            No exhibits yet.
          </Text>
        ) : (
          <ScrollArea>
            <DragDropContext
              onDragEnd={({ source, destination }) => {
                if (!destination || destination.index === source.index) {
                  return
                }
                move(
                  {
                    exhibit: submission.exhibits[source.index],
                    to: destination.index,
                  },
                  {
                    onError: error =>
                      showNotification({
                        color: 'red',
                        message: getApiErrorMessage(
                          error,
                          'Could not move the exhibit',
                        ),
                      }),
                  },
                )
              }}
            >
              <Table verticalSpacing="xs" className="min-w-[1040px]">
                <thead>
                  <tr>
                    <th className="w-10" />
                    <th className="w-12">Order</th>
                    <th>Exhibit number</th>
                    <th className="w-56">File</th>
                    <th>Description</th>
                    <th className="w-32">Designation</th>
                    <th className="w-28">Inclusion</th>
                    <th>Notes</th>
                    <th className="w-20" />
                  </tr>
                </thead>
                <Droppable droppableId="exhibits">
                  {provided => (
                    <tbody ref={provided.innerRef} {...provided.droppableProps}>
                      {submission.exhibits.map((exhibit, position) => (
                        <Draggable
                          key={exhibit.id}
                          draggableId={exhibit.id}
                          index={position}
                          isDragDisabled={!exhibit.editable}
                        >
                          {(draggable, snapshot) => (
                            <tr
                              ref={draggable.innerRef}
                              {...draggable.draggableProps}
                              className={
                                snapshot.isDragging
                                  ? 'table bg-white shadow-md'
                                  : undefined
                              }
                            >
                              <td {...draggable.dragHandleProps}>
                                {exhibit.editable ? (
                                  <span
                                    aria-label={`Drag ${exhibit.name}`}
                                    className="inline-flex cursor-grab text-gray-500"
                                  >
                                    <DragHandleDots2Icon />
                                  </span>
                                ) : (
                                  <ReadOnlyExhibitIcon />
                                )}
                              </td>
                              <td>{position + 1}</td>
                              <td>
                                <Text size="sm" weight={600}>
                                  {exhibit.name}
                                </Text>
                              </td>
                              <td className="max-w-[14rem]">
                                <ExhibitFileLink
                                  exhibit={exhibit}
                                  onPreview={() =>
                                    setPreviewing(exhibitPreviewFile(exhibit))
                                  }
                                />
                              </td>
                              <td>
                                <Text size="sm" lineClamp={2}>
                                  {exhibit.description || '—'}
                                </Text>
                              </td>
                              <td>{DESIGNATION_LABELS[exhibit.designation]}</td>
                              <td>
                                {exhibit.inclusion === 'unknown'
                                  ? '—'
                                  : INCLUSION_LABELS[exhibit.inclusion]}
                              </td>
                              <td>
                                {exhibit.notes ? (
                                  <ClampedText text={exhibit.notes} lines={2} />
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td>
                                {exhibit.editable && (
                                  <ExhibitActions
                                    event={event}
                                    exhibit={exhibit}
                                    onEdit={() => setEditing(exhibit)}
                                  />
                                )}
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </tbody>
                  )}
                </Droppable>
              </Table>
            </DragDropContext>
          </ScrollArea>
        )}
      </Modal>

      <ExhibitFilePreviewModal
        file={previewing}
        onClose={() => setPreviewing(null)}
      />
      <EditExhibitModal
        event={event}
        exhibit={editing}
        onClose={() => setEditing(null)}
      />
    </>
  )
}

const ExhibitActions: React.FC<{
  event: ExhibitSubmissionEvent
  exhibit: CourtReporterExhibit
  onEdit: () => void
}> = ({ event, exhibit, onEdit }) => {
  const { openConfirmModal } = useModals()
  const { mutate: remove, isLoading } = useRemoveExhibitMutation(event)

  return (
    <Group spacing={4} noWrap position="right">
      <ActionIcon aria-label={`Edit ${exhibit.name}`} onClick={onEdit}>
        <Pencil1Icon />
      </ActionIcon>
      <ActionIcon
        aria-label={`Delete ${exhibit.name}`}
        color="red"
        loading={isLoading}
        onClick={() =>
          openConfirmModal({
            title: `Delete exhibit ${exhibit.name}?`,
            children: (
              <Text size="sm">
                {exhibit.file
                  ? 'Its file is deleted too.'
                  : 'The exhibit is removed from the event.'}
              </Text>
            ),
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            confirmProps: { color: 'red' },
            onConfirm: () =>
              remove(exhibit, {
                onError: error =>
                  showNotification({
                    color: 'red',
                    message: getApiErrorMessage(
                      error,
                      'Could not delete the exhibit',
                    ),
                  }),
              }),
          })
        }
      >
        <TrashIcon />
      </ActionIcon>
    </Group>
  )
}
